import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Avatar from "@/components/common/Avatar";
import { useDispatch } from "react-redux";
import WebIcon from "@/components/common/Web_Icons";
import Button from "@/components/common/Button";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import { useSupportTicketChat } from "@/hooks/useSupportTicketChat";
import { updateTicketStatusAsync } from "@/store/redux/slices/support/support_slice";
import SupportChatMessage from "@/components/layout/Header/support/SupportChatMessage";
import SupportChatInput from "@/components/layout/Header/support/SupportChatInput";

const SupportChatView = ({ ticketId, onBack, onTicketClosed }) => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();
  const { ticket, ticketReplies, isLoading, replyLoading, sendReply } =
    useSupportTicketChat(ticketId);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket, ticketReplies, isLoading]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [localStatus, setLocalStatus] = useState(null);

  const status = localStatus || ticket?.status;
  const isClosed = status === "Closed";

  const handleCloseTicket = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteProceed = async () => {
    try {
      showLoader();
      setShowDeleteModal(false);
      await dispatch(
        updateTicketStatusAsync({
          id: ticket?._id || ticketId,
          status: "Closed",
        })
      ).unwrap();
      setLocalStatus("Closed");
      setShowSuccessModal(true);
      onTicketClosed?.();
    } catch (error) {
      console.error("Error closing ticket:", error);
    } finally {
      hideLoader();
    }
  };

  const handleSendReply = async (message) => {
    try {
      await sendReply(message);
    } catch (error) {
      console.error("Error sending reply:", error);
    }
  };

  if (isLoading && !ticket) {
    return (
      <div className="flex items-center justify-center h-full py-8">
        <p className="text-small text-lighterGrey">Loading conversation...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-0 mb-4">
        <button onClick={onBack} className="flex items-center gap-1" type="button">
          <WebIcon icon="ArrowLeft" className="w-4 h-4" />
          <span className="text-black text-small">BACK</span>
        </button>
      </div>

      <div className="flex items-center gap-4 p-4 border-b mb-4">
        <Avatar
          src={ticket?.userID?.profileImage || undefined}
          alt={ticket?.userID?.fullname || ""}
          size={48}
        />
        <div className="flex-grow min-w-0">
          <h2 className="font-medium text-bold truncate text-primary">
            {ticket?.userID?.fullname || ""}
          </h2>
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${
                isClosed ? "bg-danger" : "bg-green"
              }`}
            />
            <span className="text-lighterGrey text-small">{status || ""}</span>
          </div>
        </div>

        <Button
          text="CLOSE"
          icon={
            <WebIcon icon="DocumentCode" size="small" className="w-3 h-3" />
          }
          iconPosition="left"
          customBgColor="white"
          customTextColor="primary"
          className="h-8 !px-3 !py-1 text-small-xs border border-primary"
          disabled={isClosed}
          onClick={handleCloseTicket}
        />
      </div>

      <div className="flex-1 flex flex-col justify-end space-y-4 overflow-y-auto mb-4 min-h-0">
        <SupportChatMessage
          message={ticket?.description || ""}
          timestamp={ticket?.createdAt}
          isAdmin={false}
        />

        {ticketReplies.map((reply, index) => (
          <SupportChatMessage
            key={reply._id || reply.id || index}
            message={reply?.description || ""}
            timestamp={reply?.createdAt}
            isAdmin={true}
            showSeen={index === ticketReplies.length - 1}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      <SupportChatInput
        disabled={isClosed}
        sending={replyLoading}
        onSend={handleSendReply}
      />

      <Delete
        isVisible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onProceed={handleDeleteProceed}
        header="Close Ticket?"
        paragraph="By closing this ticket, it means the issue at hand has been resolved and the customer will be alerted about this closure"
        proceedButtonText="Proceed"
        cancelButtonText="Cancel"
      />

      <Success
        isVisible={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        header="Ticket Closed"
        paragraph='The ticket owner will be alerted about the closure of the ticket as the issuer has been marked as <strong>"solved"</strong>.'
        buttonText="Close"
      />
    </div>
  );
};

SupportChatView.propTypes = {
  ticketId: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onTicketClosed: PropTypes.func,
};

export default SupportChatView;
